import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { PreorderDeliveryMethod } from '../models/cart.model';

const STORAGE_KEY = 'perron.checkout.delivery_method';

const VALID_METHODS: ReadonlySet<PreorderDeliveryMethod> = new Set([
  'ps',
  'uber',
  'other_shop',
  'home',
  'ps_delivery',
]);

/**
 * Persistencia del último método de envío usado por el cliente, para
 * preseleccionarlo por defecto en el siguiente checkout.
 *
 * Política:
 *  - SET solo tras una compra exitosa (lo hace la página de éxito con el
 *    `delivery_method` del preorder ya materializado).
 *  - Aplica a TODOS los clientes (invitados y registrados): es una
 *    preferencia de UX, no un dato de identidad atado a la sesión.
 *  - SSR-safe: en server, getter devuelve null y setter es no-op.
 *  - Sanitiza contra la whitelist de métodos válidos: si el valor guardado
 *    está corrupto o no pertenece al set, devuelve null (graceful).
 */
@Injectable({ providedIn: 'root' })
export class DeliveryPreferenceService {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  getLastMethod(): PreorderDeliveryMethod | null {
    if (!this.isBrowser) return null;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw && VALID_METHODS.has(raw as PreorderDeliveryMethod)) {
        return raw as PreorderDeliveryMethod;
      }
      return null;
    } catch {
      return null;
    }
  }

  setLastMethod(method: PreorderDeliveryMethod): void {
    if (!this.isBrowser) return;
    try {
      localStorage.setItem(STORAGE_KEY, method);
    } catch {
      // Quota exceeded o modo privado: silencioso para no bloquear el flujo
      // de compra exitoso.
    }
  }
}
